import { useState } from 'react';
import { KeyRound, Trash2, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import {
  useDeleteTenantUserMutation,
  useResetTenantUserPasswordMutation,
  useSetTenantUserEnabledMutation,
} from '../../features/tenant/mutations/useTenantUserMutations';
import { useI18n } from '../../i18n/useI18n';
import { toastMutationError } from '../../query/mutationToasts';
import { toast } from '../../toast/toast';
import type { CurrentUserProfile } from '../../types/user';
import { ResetUserPasswordDialog } from '../../features/user/components/ResetUserPasswordDialog';
import { resolveTenantTabPath } from '../../routes/tenantPaths';
import './TenantUserDetailsPage.css';

export type TenantUserDetailsAdminBarProps = {
  profile: CurrentUserProfile;
  userId: string;
};

export function TenantUserDetailsAdminBar({
  profile,
  userId,
}: TenantUserDetailsAdminBarProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const deleteUserMutation = useDeleteTenantUserMutation();
  const resetPasswordMutation = useResetTenantUserPasswordMutation();
  const setEnabledMutation = useSetTenantUserEnabledMutation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  const isBusy =
    deleteUserMutation.isPending ||
    resetPasswordMutation.isPending ||
    setEnabledMutation.isPending;
  const isUserEnabled = profile.enabled !== false;
  const username =
    profile.username?.trim() ||
    [profile.firstName, profile.lastName]
      .filter((part): part is string => Boolean(part?.trim()))
      .join(' ') ||
    userId;

  const handleToggleEnabled = () => {
    const nextEnabled = !isUserEnabled;
    setEnabledMutation.mutate(
      {
        userId,
        profile,
        enabled: nextEnabled,
      },
      {
        onSuccess: () => {
          toast.success({
            title: nextEnabled
              ? translate(
                  'organisationManagement.users.details.enableSuccessTitle',
                )
              : translate(
                  'organisationManagement.users.details.disableSuccessTitle',
                ),
            description: nextEnabled
              ? translate(
                  'organisationManagement.users.details.enableSuccessDescription',
                  { username },
                )
              : translate(
                  'organisationManagement.users.details.disableSuccessDescription',
                  { username },
                ),
          });
        },
        onError: (error) => {
          void toastMutationError(
            error,
            nextEnabled
              ? translate(
                  'organisationManagement.users.details.enableErrorTitle',
                )
              : translate(
                  'organisationManagement.users.details.disableErrorTitle',
                ),
            nextEnabled
              ? translate(
                  'organisationManagement.users.details.enableErrorDescription',
                )
              : translate(
                  'organisationManagement.users.details.disableErrorDescription',
                ),
          );
        },
      },
    );
  };

  const handleResetPassword = (password: string, temporary: boolean) => {
    resetPasswordMutation.mutate(
      {
        userId,
        password,
        temporary,
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate(
              'organisationManagement.users.details.resetPasswordSuccessTitle',
            ),
            description: translate(
              'organisationManagement.users.details.resetPasswordSuccessDescription',
              { username },
            ),
          });
          setIsResetPasswordOpen(false);
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate(
              'organisationManagement.users.details.resetPasswordErrorTitle',
            ),
          );
        },
      },
    );
  };

  const handleDeleteUser = () => {
    deleteUserMutation.mutate(userId, {
      onSuccess: () => {
        toast.success({
          title: translate(
            'organisationManagement.users.details.deleteSuccessTitle',
          ),
          description: translate(
            'organisationManagement.users.details.deleteSuccessDescription',
            { username },
          ),
        });
        setIsDeleteConfirmOpen(false);
        navigate(resolveTenantTabPath('users'));
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('organisationManagement.users.details.deleteErrorTitle'),
          translate(
            'organisationManagement.users.details.deleteErrorDescription',
          ),
        );
      },
      onSettled: () => {
        setIsDeleteConfirmOpen(false);
      },
    });
  };

  return (
    <>
      <div className="tenant-user-details__admin-actions">
        <LiksButton
          type="button"
          variant="secondary"
          disabled={isBusy}
          onClick={handleToggleEnabled}
        >
          {isUserEnabled && <UserX strokeWidth={2} aria-hidden />}
          {isUserEnabled
            ? translate('organisationManagement.users.details.disable')
            : translate('organisationManagement.users.details.enable')}
        </LiksButton>
        <LiksButton
          type="button"
          variant="secondary"
          disabled={isBusy}
          onClick={() => setIsResetPasswordOpen(true)}
        >
          <KeyRound strokeWidth={2} aria-hidden />
          {translate('organisationManagement.users.details.resetPassword')}
        </LiksButton>
        <LiksButton
          type="button"
          variant="secondary"
          className="tenant-user-details__delete-button"
          disabled={isBusy}
          onClick={() => setIsDeleteConfirmOpen(true)}
        >
          <Trash2 strokeWidth={2} aria-hidden />
          {translate('organisationManagement.users.details.delete')}
        </LiksButton>
      </div>

      {isResetPasswordOpen && (
        <ResetUserPasswordDialog
          onReset={handleResetPassword}
          onClose={() => setIsResetPasswordOpen(false)}
          isSubmitting={resetPasswordMutation.isPending}
        />
      )}

      <LiksConfirmDialog
        isOpen={isDeleteConfirmOpen}
        title={translate(
          'organisationManagement.users.details.deleteConfirmTitle',
        )}
        message={translate(
          'organisationManagement.users.details.deleteConfirmMessage',
          { username },
        )}
        confirmLabel={translate(
          'organisationManagement.users.details.deleteConfirm',
        )}
        cancelLabel={translate(
          'organisationManagement.users.details.deleteCancel',
        )}
        onConfirm={handleDeleteUser}
        onCancel={() => {
          if (!deleteUserMutation.isPending) {
            setIsDeleteConfirmOpen(false);
          }
        }}
      />
    </>
  );
}
